/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback} from 'react';
import {Box, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import Render from '@ion/dls/src/render/Render';
import {dlsAlertColor, dlsColor, dlsMutedColor} from '@ion/dls/src/theme/theme';
import {useThemeMode} from '@ion/dls/src/theme/useThemeContext';
import {style} from './SentinelAssociationTab';
import {getPrimaryTextColor} from './SentinelColors';

interface SentinelInfoProps {
  icon?: React.JSX.Element;
  label?: string;
  value?: string;
  lastUpdateDate?: string;
  isSecondarySilent?: boolean;
};

interface StyleProps {
  isDark: boolean;
};

const useStyles = makeStyles(() => {
  return {
    sentinelIconStyle : {
      display : 'flex'
    },
    sentinelTitleInfoStyle : {
      ...style,
      color      : ({isDark}: StyleProps) => getPrimaryTextColor(isDark),
      fontSize   : '16px !important',
      lineHeight : '20px',
      ml         : 3
    },
    sentinelContentInfoStyle : {
      ...style,
      color      : ({isDark}: StyleProps) => dlsMutedColor(isDark),
      fontSize   : '16px !important',
      lineHeight : '20px',
      ml         : 3
    },
    sentinelLastUpdateStyle : {
      fontFamily    : 'Roboto !important',
      width         : '130px',
      fontWeight    : 500,
      letterSpacing : '0.5px',
      verticalAlign : 'middle',
      color         : ({isDark}: StyleProps) => dlsMutedColor(isDark),
      fontSize      : '9px',
      lineHeight    : '16px',
      textTransform : 'uppercase'
    },
    sentinelContentStyle : {
      display       : 'flex',
      flexDirection : 'column',
      alignItems    : 'flex-start'
    }
  };
});

const getHighlightBorder = (isDark: boolean): string => (
  `2px solid ${dlsAlertColor(isDark)}`
);

const sentinelDetailBoxStyles = (isSecondarySilent: boolean, showHighlightWidget: boolean, isDark: boolean) => ({
  p               : 1.5,
  height          : '68px',
  backgroundColor : isSecondarySilent && showHighlightWidget
    ? dlsColor('white')
    : dlsColor('grey6'),
  borderRadius   : '4px',
  border         : isSecondarySilent && showHighlightWidget ? getHighlightBorder(isDark) : 'none',
  display        : 'flex',
  flexDirection  : 'column',
  justifyContent : 'center',
  alignItems     : 'flex-start',
  marginRight    : '4px',
  marginBottom   : '4px'
});

/**
 * A component for displaying a labeled field with an icon and value, typically
 * used for showing device attributes such as battery level, temperature,
 * or installation date in Sentinel device management UIs.
 *
 * ```
 * 
 * <SentinelInfo
 *   icon={<BatteryIcon />}
 *   label="Battery Level"
 *   value="85%"
 * />
 * 
 * ```
 *
 */
const SentinelInfo = ({
  icon,
  label,
  value,
  lastUpdateDate,
  isSecondarySilent = false
}: SentinelInfoProps) => {
  const {isDark}     = useThemeMode();
  const classes      = useStyles({isDark});
  const detailsLabel = translate('admin-devices:view.sentinel.detailsTab', {returnObjects: true});

  const showHighlightWidget = useCallback(() => {
    return label?.indexOf(detailsLabel?.batterylevel) !== -1 ||
      label?.indexOf(detailsLabel?.temperature) !== -1;
  }, [label]);

  return (
    <>
      <div className={classes.sentinelContentStyle}>
        <Box sx={sentinelDetailBoxStyles(isSecondarySilent, showHighlightWidget(), isDark)}>
          <Typography className={classes.sentinelTitleInfoStyle}>{label} : </Typography>
          <div className={classes.sentinelIconStyle}>
            <span>{icon}</span>
            <Typography className={classes.sentinelContentInfoStyle}>{value}</Typography>
          </div>
        </Box>
        <Render.If condition={isSecondarySilent && showHighlightWidget()}>
          <span className={classes.sentinelLastUpdateStyle}>
            {translate('admin-devices:view.sentinel.lastUpdatedTime')}{lastUpdateDate}
          </span>
        </Render.If>
      </div>
    </>
  );
};

export default React.memo(SentinelInfo);
